import axios from "axios";

const STREAM_BASE_URL =
  process.env.REACT_APP_STREAM_BASE_URL || "https://streamapi.powerplay.axra.com.mm";

export const isMp4Url = (url) => {
  if (!url || typeof url !== "string") return false;
  try {
    const pathname = new URL(url).pathname || "";
    return /\.mp4$/i.test(pathname);
  } catch {
    return /\.mp4(\?|#|$)/i.test(url);
  }
};

/**
 * Derive sourceKey from the upload URL for POST /hls/segment.
 * Uses the path part of the URL (no leading slash) so the Stream Service
 * can resolve the file in its storage (same origin or configured base).
 * @param {string} uploadUrl - Full URL returned by upload API (e.g. imageUploadUrl)
 * @returns {string} Path relative to storage (e.g. "uploads/abc.mp4")
 */
export const getSourceKeyFromUploadUrl = (uploadUrl) => {
  if (!uploadUrl || typeof uploadUrl !== "string") return "";
  try {
    const pathname = new URL(uploadUrl).pathname || "";
    const key = pathname.replace(/^\//, "").trim();
    // If stream storage uses a flat key, strip common prefixes like "storage/".
    return key.startsWith("storage/") ? key.slice("storage/".length) : key;
  } catch {
    // If it's a relative path or non-URL string, use it as-is.
    const key = uploadUrl.replace(/^\//, "").trim();
    return key.startsWith("storage/") ? key.slice("storage/".length) : key;
  }
};

/**
 * Call Stream Service POST /hls/segment to create HLS from a source video.
 * @param {{ videoId: string, sourceKey: string }} params - videoId (e.g. record id or "id-b"), sourceKey from getSourceKeyFromUploadUrl
 * @returns {Promise<{ manifestUrl: string, manifestKey: string }>}
 * @throws {Error} on network or API error (4xx/5xx or error body)
 */
export const segmentVideo = async ({ videoId, sourceKey }) => {
  if (!videoId || !sourceKey) {
    throw new Error("segmentVideo requires videoId and sourceKey");
  }
  const url = `${STREAM_BASE_URL}/hls/segment`;
  const response = await axios.post(
    url,
    { videoId, sourceKey },
    { headers: { "Content-Type": "application/json" } }
  );
  const data = response.data;
  if (!data.manifestUrl) {
    throw new Error("Segment API did not return manifestUrl");
  }
  return { manifestUrl: data.manifestUrl, manifestKey: data.manifestKey };
};

export default { segmentVideo, getSourceKeyFromUploadUrl, isMp4Url };
